import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ChangeEvent,
} from 'react';
import { Select, Divider } from 'antd';
import PhoneInput from 'react-phone-input-2';
import { TransferToEscalationOption } from '@/proto/logiblocks/logiblocks';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import useToken from '@/common/helper/useToken';
import { GetAgentsWithPaginationAndSearch } from '@/apis/settings';
import type { ApiAgent } from '@/pages/Settings/AiAgents/types';
import { FieldLabel } from '@/pages/Logiblocks/nodes/shared/FieldLabel';
import { FieldErrorLabel } from '@/pages/Logiblocks/nodes/shared/FieldErrorLabel';
import ToggleSwitch from '@/components/ToggleSwitch';
import styles from '../NodeCustomization.module.css';
import '@/pages/Settings/AiAgents/AgentsTable.css';
import 'react-phone-input-2/lib/style.css';
import { TransferToActionFields } from './TransferToActionNodeInfo';

const ESCALATION_OPTIONS = [
  {
    value: TransferToEscalationOption.TRY_SECOND_NUMBER,
    label: 'Try a second number',
  },
  {
    value: TransferToEscalationOption.CONNECT_TO_AGENT,
    label: 'Connect to an agent',
  },
];

// PhoneInput stores digits without '+'; proto stores E.164 with '+'
const toPhoneInputValue = (e164: string) => e164.replace(/^\+/, '');
const toE164 = (digits: string) => (digits ? `+${digits}` : '');

export const TransferToActionCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const { token } = useToken();

  const rawData = nodeData.transfer_to_action;
  const currentData = useMemo(
    () => ({
      primary_number: rawData?.primary_number ?? '',
      enable_escalation: rawData?.enable_escalation ?? false,
      escalation_option:
        rawData?.escalation_option ??
        TransferToEscalationOption.TRANSFER_TO_ESCALATION_OPTION_UNSPECIFIED,
      second_number: rawData?.second_number ?? '',
      agent_id: rawData?.agent_id ?? '',
    }),
    [rawData]
  );

  const notifyChange = useCallback(
    (updates: Partial<typeof currentData>) => {
      onChange({ transfer_to_action: { ...currentData, ...updates } });
    },
    [currentData, onChange]
  );

  const [inboundAgents, setInboundAgents] = useState<ApiAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoadingAgents(true);
    GetAgentsWithPaginationAndSearch(token, '', 1)
      .then((res) => {
        const all: ApiAgent[] = res?.data?.data?.data ?? [];
        setInboundAgents(all.filter((a) => a.direction === 'Inbound'));
      })
      .catch(() => setInboundAgents([]))
      .finally(() => setLoadingAgents(false));
  }, [token]);

  const primaryNumberError = validationErrors?.find(
    (e) => e.field === TransferToActionFields.PRIMARY_NUMBER
  );
  const secondNumberError = validationErrors?.find(
    (e) => e.field === TransferToActionFields.SECOND_NUMBER
  );
  const agentError = validationErrors?.find(
    (e) => e.field === TransferToActionFields.AI_AGENT
  );

  const showTrySecondNumber =
    currentData.enable_escalation &&
    currentData.escalation_option ===
      TransferToEscalationOption.TRY_SECOND_NUMBER;

  const showConnectToAgent =
    currentData.enable_escalation &&
    currentData.escalation_option ===
      TransferToEscalationOption.CONNECT_TO_AGENT;

  return (
    <>
      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Primary Number
        </FieldLabel>
        <PhoneInput
          country='us'
          value={toPhoneInputValue(currentData.primary_number)}
          onChange={(digits) =>
            notifyChange({ primary_number: toE164(digits) })
          }
          disabled={isReadOnly}
        />
        <FieldErrorLabel error={primaryNumberError} />
      </div>

      <Divider className='agentFormDivider' style={{ margin: '16px 0' }} />

      <div className={styles.customizationSection}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label className={styles.sectionLabel} style={{ marginBottom: 0 }}>
            Enable voice escalation options
          </label>
          <ToggleSwitch
            checked={currentData.enable_escalation}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              notifyChange({ enable_escalation: e.target.checked })
            }
            disabled={isReadOnly}
          />
        </div>
        <p
          style={{
            fontSize: '10px',
            color: 'var(--text-secondary)',
            margin: '2px 0 0 0',
          }}
        >
          If voicemail or no response
        </p>
      </div>

      {currentData.enable_escalation && (
        <>
          <div className={styles.customizationSection}>
            <FieldLabel className={styles.sectionLabel}>
              Escalation options
            </FieldLabel>
            <Select
              className='agentSelect agentSelect--darkText'
              value={
                currentData.escalation_option ===
                TransferToEscalationOption.TRANSFER_TO_ESCALATION_OPTION_UNSPECIFIED
                  ? undefined
                  : currentData.escalation_option
              }
              disabled={isReadOnly}
              placeholder='Select escalation option'
              options={ESCALATION_OPTIONS}
              onChange={(value: TransferToEscalationOption) =>
                notifyChange({
                  escalation_option: value,
                  second_number: '',
                  agent_id: '',
                })
              }
            />
          </div>

          {showTrySecondNumber && (
            <div
              className={styles.customizationSection}
              style={{
                borderLeft: '2px solid rgba(0, 0, 0, 0.08)',
                paddingLeft: 14,
              }}
            >
              <FieldLabel required className={styles.sectionLabel}>
                Secondary number
              </FieldLabel>
              <PhoneInput
                country='us'
                value={toPhoneInputValue(currentData.second_number)}
                onChange={(digits) =>
                  notifyChange({ second_number: toE164(digits) })
                }
                disabled={isReadOnly}
              />
              <FieldErrorLabel error={secondNumberError} />
            </div>
          )}

          {showConnectToAgent && (
            <div
              className={styles.customizationSection}
              style={{
                borderLeft: '2px solid rgba(0, 0, 0, 0.08)',
                paddingLeft: 14,
              }}
            >
              <FieldLabel required className={styles.sectionLabel}>
                AI agent
              </FieldLabel>
              <Select
                value={currentData.agent_id || undefined}
                onChange={(value: string) => notifyChange({ agent_id: value })}
                placeholder={
                  loadingAgents ? 'Loading agents...' : 'Select an agent'
                }
                disabled={isReadOnly || loadingAgents}
                style={{ width: '100%' }}
                className='agentSelect'
              >
                {inboundAgents.length > 0 && (
                  <Select.OptGroup label='Inbound'>
                    {inboundAgents.map((agent) => (
                      <Select.Option key={agent.id} value={agent.id}>
                        {agent.name || agent.id}
                        {agent.purpose_name && ` (${agent.purpose_name})`}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                )}
              </Select>
              <FieldErrorLabel error={agentError} />
              <p
                style={{ fontSize: '12px', marginTop: '8px', marginBottom: 0 }}
              >
                Create and customize{' '}
                <a
                  href='/settings/ai-agent'
                  target='_blank'
                  rel='noreferrer'
                  style={{ textDecoration: 'underline', color: 'inherit' }}
                >
                  AI agent settings
                </a>
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};
